import React, {FC, useEffect, useState} from "react"
import {debounce, uniqBy} from "lodash"

import Select, {SelectProps} from "@irati/ui/src/FormItem/Select"
import {ALL_TEAMS} from "@irati-web/admin/src/components/Reporting/Region"
import useAuth from "@irati/auth/helpers/auth/useAuth"
import {useLazyTeamsPaginated} from "@irati/identity/hooks"
import {useIntl} from "@irati/utils/lang"

interface Props extends SelectProps {
  includeAllTeams?: boolean
}

const TeamSearch: FC<Props> = ({onChange, isDisabled, value, isCreatable, includeAllTeams = true, ...selectProps}) => {
  const {formatMessage} = useIntl()
  const [teamName, setTeamName] = useState("")
  const [cursor, setCursor] = useState(null)
  const [searchCursor, setSearchCursor] = useState(null)
  const [searchAfter, setSearchAfter] = useState(null)
  const [options, setOptions] = useState(includeAllTeams ? [{label: formatMessage({id: "platform.allTeams"}), value: ALL_TEAMS}] : [])
  const [selectedValue, setSelectedValue] = useState(ALL_TEAMS)
  const [isLoading, setIsLoading] = useState(false)
  const {business} = useAuth()
  const [teamsPaginated, apollo] = useLazyTeamsPaginated({
    businessId: business.uuid,
    teamName,
    searchAfter
  })

  useEffect(() => {
    teamsPaginated()
  }, [])

  useEffect(() => {
    setIsLoading(false)
    if (apollo.data) {
      const baseTeamOptions = apollo.data.teams.map((team) => {
        return {label: team.teamName, value: team.uuid}
      })
      setOptions(uniqBy([...options, ...baseTeamOptions], "value"))

      if (apollo.data?.searchAfter) {
        if (teamName) {
          setSearchCursor(apollo.data.searchAfter)
        } else {
          setCursor(apollo.data.searchAfter)
        }
      } else {
        if (teamName) {
          setSearchCursor(null)
        } else {
          setCursor(null)
        }
      }
    }
  }, [apollo?.data])

  useEffect(() => {
    if (value !== selectedValue) {
      setSelectedValue(value)
    }
  }, [value])

  const getNextPage = () => {
    const nextCursor = teamName ? searchCursor : cursor
    setSearchAfter(nextCursor)
  }

  const updateInput = debounce((inputValue) => {
    setTeamName(inputValue)
  }, 1000)

  const handleInputChange = (inputValue, {action}) => {
    if (action === "input-change") {
      setIsLoading(true)
      updateInput.cancel()
      updateInput(inputValue)
    } else if (action === "set-value" || action === "input-blur") {
      updateInput.cancel()
    }
  }

  const handleChange = (updatedValue: string) => {
    updateInput.cancel()
    if (onChange) {
      onChange(updatedValue)
    }
  }

  return (
    <Select
      value={selectedValue}
      options={options}
      onInputChange={handleInputChange}
      onMenuScrollToBottom={getNextPage}
      isLoading={isCreatable ? undefined : isLoading}
      onChange={handleChange}
      autoComplete={false}
      aria-label="Team Search"
      isDisabled={isDisabled}
      isCreatable={isCreatable}
      {...selectProps}
    />
  )
}

export default TeamSearch
